import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux'

import { CountryCode } from 'libphonenumber-js'
import { capitalize } from 'lodash-es'

import { getDateFromAppointment } from '@lyrahealth-inc/shared-app-logic/src/features/appointments/getDateFromAppointment'
import {
  getCaregiverContactFromIntakeForm,
  getGuardianInfoForEpisode,
} from '@lyrahealth-inc/shared-app-logic/src/features/appointments/utils'
import { getClientPreferredNameIfExists } from '@lyrahealth-inc/shared-app-logic/src/features/assignments/utils'
import { formatPhoneNumberForDisplay } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/stringUtils'
import { getTimeZoneName } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/timeZoneUtils'
import { formatDOB, formatPronouns } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/userUtils'
import { hasSelfHarmFromLatestOutcome } from '@lyrahealth-inc/shared-app-logic/src/features/outcomes/utils'
import { affirmativeResponses } from '@lyrahealth-inc/shared-app-logic/src/features/profile/constants'
import { getProgramDisplayLabel } from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programHelpers'
import { getAge } from '@lyrahealth-inc/shared-app-logic/src/features/searchForCare/utils'
import { Appointment } from '@lyrahealth-inc/shared-app-logic/src/models/appointment/Appointment'
import { Assignment } from '@lyrahealth-inc/shared-app-logic/src/models/assignments/Assignments'
import { ClientObject } from '@lyrahealth-inc/shared-app-logic/src/models/client/Client'
import { Episode } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'
import { ProgramNames } from '@lyrahealth-inc/shared-app-logic/src/models/programs/Programs'
import {
  RISK_BADGE_TEXT,
  SELF_HARM_BADGE_TEXT,
} from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/profileCardBadge/ProfileCardBadge'
import { Details } from '@lyrahealth-inc/ui-core-crossplatform/src/ui-models/messages/Details'

import { useCompaniesWithWarnings } from '../../../common/components/hooks/useCompaniesWithWarnings'
import {
  companyMessages,
  episodeStates,
  MILITARY_ONE_SOURCE_EMPLOYER,
  programConfig,
  programs,
} from '../../../common/constants/appConstants'
import { getGSCDuration } from '../../../common/utils/utils'
import { getAuthMosApproved } from '../../../data/auth/authSelectors'
import { pickEpisode } from '../../episodes/data/pickEpisode'

type PanelArgs = {
  client: ClientObject | null
  episodes: Episode[] | undefined
  isEngageDAProvider?: boolean
  appointments?: Appointment[]
  assignments?: Assignment[]
  hasClientSelfHarmTask?: boolean
}
const useDetailsPanelData: (args: PanelArgs) => Details | null = ({
  client,
  episodes,
  isEngageDAProvider,
  appointments,
  assignments,
  hasClientSelfHarmTask,
}: PanelArgs) => {
  const intl = useIntl()
  const isMOSApprovedProvider = useSelector(getAuthMosApproved)
  const companiesWithWarnings = useCompaniesWithWarnings()
  const hasLimitedAccessSpecialAlert =
    !!client?.employer &&
    companiesWithWarnings[client.employer.toLowerCase()]?.includes(
      companyMessages.LIMITED_ACCESS_AND_MODIFIED_REFERRALS,
    )
  const episode = useMemo(() => pickEpisode(episodes, client?.id), [client?.id, episodes])

  const guardianInfo = useMemo(() => {
    const relevantAppointments = episode?.id
      ? appointments?.filter((appt) => appt.episodeId === episode.id)
      : appointments
    return getGuardianInfoForEpisode(relevantAppointments ?? [], episode?.participants, {
      includeSecondaryPatientAsGuardian: episode?.program_name === ProgramNames.DialecticalBehaviorTherapyTeens,
    })
  }, [appointments, episode?.id, episode?.participants, episode?.program_name])

  const intakeCaregiverContact = useMemo(
    () => getCaregiverContactFromIntakeForm(assignments, episode?.id),
    [assignments, episode?.id],
  )

  return useMemo(() => {
    if (!client) {
      return null
    }

    const computedAge = getAge({ date: client.date_of_birth, dateFormat: '' })
    const age = Number.isNaN(computedAge) ? '' : computedAge.toString()
    const displayTimeZone = client.time_zone_full
      ? `${getTimeZoneName({ timeZone: client.time_zone_full, timeZoneName: 'long' })} (${getTimeZoneName({
          timeZone: client.time_zone_full,
          timeZoneName: 'short',
        })})`
      : ''
    const isDAFamilyMinor =
      episode?.program_name === ProgramNames.DirectAccessFamilyTherapy && !Number.isNaN(computedAge) && computedAge < 18
    const isDAFamilyTeenLed = isDAFamilyMinor && computedAge >= 13 && !guardianInfo?.guardianLyraId
    const isNonTeenMinor = episode?.program_name === ProgramNames.DirectAccessIndividualChildTherapy || isDAFamilyMinor
    const isTeenLedDA = episode?.program_name === ProgramNames.DirectAccessIndividualAdolescentTherapy
    const phoneNumber = formatPhoneNumberForDisplay(client.phone, (client.country as CountryCode) || 'US') || null
    const hideClientPhone = (isNonTeenMinor && !isDAFamilyTeenLed) || (isTeenLedDA && phoneNumber === null)
    const email = isEngageDAProvider ? client.product_email || (client.email ? client.email : 'N/A') : client.email

    return {
      client: {
        legalName: client.full_name,
        preferredName: getClientPreferredNameIfExists(client),
        timeZone: client.time_zone ?? null,
        dateOfBirth: client.date_of_birth,
        phoneNumber,
        showRiskBadge: isEngageDAProvider
          ? !!episode?.risk_flag || !!client.self_harm || hasSelfHarmFromLatestOutcome(client.outcomes)
          : !!client.self_harm || !!hasClientSelfHarmTask,
        riskBadgeText: isEngageDAProvider ? RISK_BADGE_TEXT : SELF_HARM_BADGE_TEXT,
        firstName: client.preferred_first_name || client.first_name,
        country:
          client?.country && client.country.toUpperCase() !== 'US' ? client.country.toLocaleUpperCase() : undefined,
        age,
        canLeaveVoicemail: client.voicemail_consent,
        dob: formatDOB(client.date_of_birth),
        gender: capitalize(client.gender),
        employer: client.employer,
        mosId: client.employer === MILITARY_ONE_SOURCE_EMPLOYER && isMOSApprovedProvider ? client.mos_id : undefined,
        lyraCode:
          client.employer === MILITARY_ONE_SOURCE_EMPLOYER && isMOSApprovedProvider ? client.lyra_code : undefined,
        sessionsUsed: client.sessions_used,
        sessionsLimit: client.sessions_limit,
        sessionCountStartsOn: client.session_count_starts_on,
        ethnicity: client.race?.join('/'),
        displayTimeZone,
        lyraId: client.id,
        pronouns: formatPronouns(client.pronouns),
        email,
        hideClientContactInfo: isNonTeenMinor && !isDAFamilyTeenLed,
        hideClientPhone,
        showSpecialAlertBadge:
          hasLimitedAccessSpecialAlert ||
          (!!client?.eligibility_fields?.nuclear_badge_flag &&
            affirmativeResponses.includes(client?.eligibility_fields?.nuclear_badge_flag)),
        specialAlertType: hasLimitedAccessSpecialAlert ? 'limitedAccess' : 'nuclearBadge',
      },
      episode: {
        isActive: episode?.state === episodeStates.IN_PROGRESS,
        program: episode?.program_name ? getProgramDisplayLabel(intl, episode.program_name) : null,
        programName: episode?.program_name ?? null,
        startDate: episode?.start_date ?? null,
        nextSession: client.next_appointment ? getDateFromAppointment(client.next_appointment).toISOString() : null,
        sessionNumber:
          episode?.program_name === programs.SingleSessionCoaching
            ? getGSCDuration(
                client?.first_appointment?.startDate,
                programConfig[episode?.program_name]?.recommendedEpisodeLength,
              )
            : client?.session_number,
        state: episode?.state ?? null,
      },
      appointment: client.next_appointment
        ? {
            startDate: client.next_appointment.startDate,
            startTime: client.next_appointment.startTime,
            timeZone: client.next_appointment.timeZone,
            meetingFormat: client.next_appointment.meetingFormat,
            appointmentStatus: client.next_appointment.appointmentStatus,
          }
        : undefined,
      guardian:
        guardianInfo?.guardianFirstName ||
        guardianInfo?.guardianLastName ||
        guardianInfo?.guardianPhone ||
        guardianInfo?.guardianEmail ||
        intakeCaregiverContact.firstName ||
        intakeCaregiverContact.lastName ||
        intakeCaregiverContact.phone ||
        intakeCaregiverContact.email
          ? {
              firstName: guardianInfo?.guardianFirstName || intakeCaregiverContact.firstName,
              lastName: guardianInfo?.guardianLastName || intakeCaregiverContact.lastName,
              phone: intakeCaregiverContact.phone
                ? formatPhoneNumberForDisplay(intakeCaregiverContact.phone, 'US')
                : guardianInfo?.guardianPhone
                  ? formatPhoneNumberForDisplay(
                      guardianInfo.guardianPhone,
                      (guardianInfo.guardianPhoneCountryCode as CountryCode) || 'US',
                    )
                  : undefined,
              email: intakeCaregiverContact.email || guardianInfo?.guardianEmail,
              canLeaveVoicemail:
                intakeCaregiverContact.canLeaveVoicemail || guardianInfo?.guardianVoicemailConsent || false,
            }
          : undefined,
    }
  }, [
    client,
    episode?.program_name,
    episode?.risk_flag,
    episode?.state,
    episode?.start_date,
    guardianInfo?.guardianLyraId,
    guardianInfo?.guardianFirstName,
    guardianInfo?.guardianLastName,
    guardianInfo.guardianPhone,
    guardianInfo?.guardianEmail,
    guardianInfo.guardianPhoneCountryCode,
    guardianInfo?.guardianVoicemailConsent,
    isEngageDAProvider,
    hasClientSelfHarmTask,
    hasLimitedAccessSpecialAlert,
    intl,
    intakeCaregiverContact.firstName,
    intakeCaregiverContact.lastName,
    intakeCaregiverContact.phone,
    intakeCaregiverContact.email,
    intakeCaregiverContact.canLeaveVoicemail,
    isMOSApprovedProvider,
  ])
}

export default useDetailsPanelData
